import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { TrendingUp, Clock, CheckCircle2, XCircle, Upload, ArrowRight, Package, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const naira = (n) => `₦${Number(n || 0).toLocaleString()}`;

const STATUS_META = {
  pending_payment: { label: 'Pending payment', color: 'bg-amber-100 text-amber-800' },
  pending:         { label: 'Pending review',  color: 'bg-amber-100 text-amber-800' },
  awaiting_verification: { label: 'Awaiting verification', color: 'bg-blue-100 text-blue-800' },
  active:          { label: 'Active',           color: 'bg-green-100 text-green-800' },
  matured:         { label: 'Matured',          color: 'bg-purple-100 text-purple-800' },
  rejected:        { label: 'Rejected',         color: 'bg-red-100 text-red-800' },
};

const InvestmentRow = ({ inv, onMarkPaid }) => {
  const status = STATUS_META[inv.status] || { label: inv.status, color: 'bg-gray-100 text-gray-700' };
  const isPending = ['pending', 'pending_payment'].includes(inv.status);
  const showAwaiting = inv.payment_status === 'awaiting_verification';

  return (
    <div
      className="p-4 border rounded-lg hover:border-green-200 transition-colors bg-white"
      data-testid={`investment-row-${inv._id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">{inv.plan_name || inv.plan_id}</p>
          <p className="text-xs text-gray-500 font-mono truncate">{inv.application_number}</p>
        </div>
        <Badge className={`${status.color} border-none flex-shrink-0`}>{status.label}</Badge>
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs mb-3">
        <div>
          <p className="text-gray-500">Amount</p>
          <p className="font-semibold">{naira(inv.amount)}</p>
        </div>
        <div>
          <p className="text-gray-500">Return</p>
          <p className="font-semibold text-green-700">+{inv.return_percentage}%</p>
        </div>
        <div>
          <p className="text-gray-500">Payout</p>
          <p className="font-semibold">{naira(inv.total_payout)}</p>
        </div>
      </div>

      {inv.status === 'active' && inv.progress && (
        <div className="space-y-1">
          <div className="flex justify-between text-[11px] text-gray-500">
            <span>Maturity progress</span>
            <span>{inv.progress.days_left} days left</span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden" role="progressbar" aria-valuenow={inv.progress.percent} aria-valuemin={0} aria-valuemax={100}>
            <div
              className="h-full bg-gradient-to-r from-green-500 to-emerald-600 transition-all"
              style={{ width: `${inv.progress.percent}%` }}
            />
          </div>
        </div>
      )}

      {isPending && !showAwaiting && (
        <div className="mt-3 pt-3 border-t flex flex-col sm:flex-row gap-2">
          <Button
            size="sm"
            className="bg-green-600 hover:bg-green-700 flex-1"
            onClick={() => onMarkPaid(inv)}
            data-testid={`mark-paid-btn-${inv._id}`}
          >
            <Upload className="h-4 w-4 mr-1" /> I&apos;ve paid — verify my transfer
          </Button>
        </div>
      )}

      {showAwaiting && (
        <div className="mt-3 pt-3 border-t flex items-center gap-2 text-xs text-blue-700 bg-blue-50 px-3 py-2 rounded-md">
          <Clock className="h-4 w-4 flex-shrink-0" />
          <span>Payment reported — our team is verifying, usually within a few hours.</span>
        </div>
      )}

      {inv.status === 'matured' && (
        <div className="mt-3 pt-3 border-t">
          <a href={`${API}/investments/${inv._id}/maturity-statement`}>
            <Button variant="outline" size="sm" className="w-full" data-testid={`download-maturity-statement-${inv._id}`}>
              <Download className="h-4 w-4 mr-2" /> Download maturity statement
            </Button>
          </a>
        </div>
      )}
    </div>
  );
};

const InvestmentsSection = ({ investments, onMarkPaid }) => {
  if (!investments || investments.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center" data-testid="investments-empty">
          <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <TrendingUp className="h-6 w-6 text-green-600" aria-hidden="true" />
          </div>
          <p className="font-semibold">No investments yet</p>
          <p className="text-sm text-gray-500 mb-4">Start earning up to 50% fixed returns with our farm-backed plans.</p>
          <Link to="/investment">
            <Button className="bg-green-600 hover:bg-green-700">
              Browse plans <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="investments-section">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-lg">My Investments</h2>
            <p className="text-xs text-gray-500">{investments.length} total</p>
          </div>
          <Link to="/investment">
            <Button variant="ghost" size="sm">New investment</Button>
          </Link>
        </div>
        <div className="space-y-3">
          {investments.map((inv) => (
            <InvestmentRow key={inv._id} inv={inv} onMarkPaid={onMarkPaid} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default InvestmentsSection;
